/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import React, {ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';

import {ActivateHandle} from '../../../../../util/interfaces';

import {COUNTRIES} from '../../../../../util/countryUtil';

import CountrySelect from './countrySelect';

interface Props {
    onSubmit: (countries: Set<string>, allSelected: boolean, callback: (close: boolean) => void) => void;
    loading: boolean;
    splitBelgiumLanguage?: boolean;
}

function splitLanguages(countries: Array<string>) {
    countries.splice(countries.indexOf('België'), 1, 'België (Nederlands)', 'België (Franstalig)');
    return countries;
}

function CountrySelectController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {onSubmit, loading, splitBelgiumLanguage} = props;
    const [allSelected, setAllSelected] = useState(false);
    const [active, setActive] = useState(false);
    const [countries, setCountries] = useState<Array<string>>([]);
    const [selectedCountries, setSelectedCountries] = useState<Set<string>>(new Set());
    const hasChanged = useRef(false);

    function onActivate(countries: Array<string>) {
        setSelectedCountries(new Set(countries));
        setAllSelected(!countries.length);
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setSelectedCountries(new Set());
        setAllSelected(false);
        hasChanged.current = false;
    }

    function onSelectCountry(country: string) {
        return function () {
            const selected = new Set(selectedCountries);
            if (selected.has(country)) selected.delete(country);
            else selected.add(country);
            setSelectedCountries(selected);
            setAllSelected(selected.size === 0);
            hasChanged.current = true;
        };
    }

    function onSelectAll() {
        setAllSelected(!allSelected);
        hasChanged.current = true;
    }

    function _onSubmit() {
        if (!hasChanged.current) return onClose();
        onSubmit(selectedCountries, allSelected, (close: boolean) => {
            if (close) onClose();
        });
    }

    useEffect(() => {
        let countryList = Object.values(COUNTRIES);
        if (splitBelgiumLanguage) countryList = splitLanguages(countryList);
        setCountries(countryList);
    }, []);

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <CountrySelect
            onSelectCountry={onSelectCountry}
            onSelectAll={onSelectAll}
            onSubmit={_onSubmit}
            onClose={onClose}
            allSelected={allSelected}
            loading={loading}
            active={active}
            countries={countries}
            selectedCountries={selectedCountries}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(CountrySelectController);
